import React, { useState } from 'react';
import { useChangePlan, useChangeStatus, useChangePortal } from '../../hooks/useCompany';
import type { Company, SubscriptionPlan, SubscriptionStatus } from '../../types';
import { PLANS, formatUsersDisplay, formatExpensesDisplay } from '../../config/plans';

interface CompanyActionsProps {
  company: Company;
  actionType: 'plan' | 'status' | 'portal';
  onClose: () => void;
}

const STATUS_LABELS: Record<SubscriptionStatus, string> = {
  active: 'Activa',
  suspended: 'Suspendida',
  cancelled: 'Cancelada',
  trial: 'Prueba',
};

const CompanyActions: React.FC<CompanyActionsProps> = ({ company, actionType, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="action-modal-title" role="dialog" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          {actionType === 'plan' ? (
            <ChangePlanModal company={company} onClose={onClose} />
          ) : actionType === 'portal' ? (
            <ChangePortalModal company={company} onClose={onClose} />
          ) : (
            <ChangeStatusModal company={company} onClose={onClose} />
          )}
        </div>
      </div>
    </div>
  );
};

function ChangePlanModal({ company, onClose }: { company: Company; onClose: () => void }) {
  const currentPlan = company.settings.subscriptionPlan;
  const [newPlan, setNewPlan] = useState<SubscriptionPlan>(currentPlan);
  const changePlan = useChangePlan();

  const isDowngrade =
    (currentPlan === 'enterprise' && newPlan !== 'enterprise') ||
    (currentPlan === 'business' && newPlan === 'starter');

  const newLimits = PLANS[newPlan];

  const handleSubmit = () => {
    if (newPlan === currentPlan) {
      onClose();
      return;
    }
    changePlan.mutate(
      { companyId: company.companyId, data: { plan: newPlan } },
      { onSuccess: () => onClose() }
    );
  };

  return (
    <>
      <h2 id="action-modal-title" className="text-lg font-semibold text-gray-900 mb-4">
        Cambiar Plan — {company.name}
      </h2>
      <div className="space-y-4">
        <div>
          <label htmlFor="new-plan" className="block text-sm font-medium text-gray-700">
            Nuevo Plan
          </label>
          <select
            id="new-plan"
            value={newPlan}
            onChange={(e) => setNewPlan(e.target.value as SubscriptionPlan)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
          >
            <option value="starter">Starter - {PLANS.starter.priceDisplay}/mes</option>
            <option value="business">Business - {PLANS.business.priceDisplay}/mes</option>
            <option value="enterprise">Enterprise - {PLANS.enterprise.priceDisplay}/mes</option>
          </select>
        </div>

        {/* Plan summary */}
        <div className="bg-gray-50 rounded-md p-3 text-sm text-gray-600">
          <p>Usuarios: hasta {formatUsersDisplay(newPlan)}</p>
          <p>Gastos mensuales: {formatExpensesDisplay(newPlan)}</p>
          <p>Precio: {newLimits.priceDisplay} CLP/mes</p>
        </div>

        {isDowngrade && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <p className="text-sm text-yellow-800">
              Atención: Este es un downgrade. Si la empresa excede los nuevos límites, los usuarios existentes no serán eliminados pero no se podrán crear nuevos hasta estar dentro del límite.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={changePlan.isLoading || newPlan === currentPlan}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {changePlan.isLoading ? 'Actualizando...' : 'Cambiar Plan'}
          </button>
        </div>
      </div>
    </>
  );
}

function ChangeStatusModal({ company, onClose }: { company: Company; onClose: () => void }) {
  const currentStatus = company.settings.subscriptionStatus;
  const [newStatus, setNewStatus] = useState<SubscriptionStatus>(currentStatus);
  const changeStatus = useChangeStatus();

  const handleSubmit = () => {
    if (newStatus === currentStatus) {
      onClose();
      return;
    }
    changeStatus.mutate(
      { companyId: company.companyId, data: { status: newStatus } },
      { onSuccess: () => onClose() }
    );
  };

  return (
    <>
      <h2 id="action-modal-title" className="text-lg font-semibold text-gray-900 mb-4">
        Cambiar Estado — {company.name}
      </h2>
      <div className="space-y-4">
        <div>
          <p className="text-sm text-gray-600 mb-2">
            Estado actual: <span className="font-medium">{STATUS_LABELS[currentStatus]}</span>
          </p>
          <label htmlFor="new-status" className="block text-sm font-medium text-gray-700">
            Nuevo Estado
          </label>
          <select
            id="new-status"
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value as SubscriptionStatus)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
          >
            <option value="active">Activa</option>
            <option value="suspended">Suspendida</option>
            <option value="cancelled">Cancelada</option>
          </select>
        </div>

        {newStatus === 'cancelled' && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-sm text-red-800">
              Atención: Cancelar la suscripción impedirá que los usuarios de esta empresa creen nuevos gastos, usuarios o proyectos.
            </p>
          </div>
        )}

        {newStatus === 'suspended' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <p className="text-sm text-yellow-800">
              Atención: Suspender la suscripción impedirá operaciones de escritura. Los datos existentes seguirán accesibles en modo lectura.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={changeStatus.isLoading || newStatus === currentStatus}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {changeStatus.isLoading ? 'Actualizando...' : 'Cambiar Estado'}
          </button>
        </div>
      </div>
    </>
  );
}

function ChangePortalModal({ company, onClose }: { company: Company; onClose: () => void }) {
  const [portalEnabled, setPortalEnabled] = useState(company.settings.portalEnabled ?? false);
  const [subdomain, setSubdomain] = useState(company.settings.customSubdomain ?? '');
  const [subdomainError, setSubdomainError] = useState('');
  const changePortal = useChangePortal();

  const validateSubdomain = (value: string): string => {
    if (!value.trim()) return '';
    const clean = value.toLowerCase().trim();
    const reserved = ['app', 'admin', 'api', 'www', 'mail', 'ftp', 'app-dev', 'admin-dev', 'api-dev'];
    if (reserved.includes(clean)) return 'Este subdominio es un nombre reservado';
    if (clean.length < 2) return 'Mínimo 2 caracteres';
    if (clean.length > 50) return 'Máximo 50 caracteres';
    if (!/^[a-z0-9-]+$/.test(clean)) return 'Solo letras, números y guiones';
    if (clean.startsWith('-') || clean.endsWith('-')) return 'No puede empezar ni terminar con guión';
    return '';
  };

  const handleSubdomainChange = (value: string) => {
    const clean = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSubdomain(clean);
    setSubdomainError(portalEnabled ? validateSubdomain(clean) : '');
  };

  const handleSubmit = () => {
    if (portalEnabled) {
      if (!subdomain.trim()) {
        setSubdomainError('El subdominio es obligatorio si habilitas el portal');
        return;
      }
      const err = validateSubdomain(subdomain);
      if (err) {
        setSubdomainError(err);
        return;
      }
    }
    changePortal.mutate(
      {
        companyId: company.companyId,
        data: {
          portalEnabled,
          customSubdomain: portalEnabled ? subdomain.trim() : undefined,
        },
      },
      { onSuccess: () => onClose() }
    );
  };

  return (
    <>
      <h2 id="action-modal-title" className="text-lg font-semibold text-gray-900 mb-4">
        Portal Personalizado — {company.name}
      </h2>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <label htmlFor="portal-toggle" className="text-sm font-medium text-gray-700">
            Habilitar portal personalizado
          </label>
          <button
            id="portal-toggle"
            type="button"
            role="switch"
            aria-checked={portalEnabled}
            onClick={() => {
              setPortalEnabled(!portalEnabled);
              if (!portalEnabled) setSubdomainError('');
            }}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              portalEnabled ? 'bg-primary-600' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                portalEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {portalEnabled && (
          <div>
            <label htmlFor="portal-subdomain" className="block text-sm font-medium text-gray-700">
              Subdominio
            </label>
            <div className="mt-1 flex rounded-md shadow-sm">
              <input
                id="portal-subdomain"
                type="text"
                value={subdomain}
                onChange={(e) => handleSubdomainChange(e.target.value)}
                className={`block w-full rounded-l-md text-sm ${
                  subdomainError
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                }`}
                placeholder="mi-empresa"
              />
              <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-50 px-3 text-sm text-gray-500">
                .kessan.cl
              </span>
            </div>
            {subdomainError && <p className="mt-1 text-sm text-red-600">{subdomainError}</p>}
            {subdomain && !subdomainError && (
              <p className="mt-1 text-sm text-gray-500">
                URL: https://{subdomain}.kessan.cl
              </p>
            )}
          </div>
        )}

        {!portalEnabled && company.settings.portalEnabled && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <p className="text-sm text-yellow-800">
              Al deshabilitar el portal, los usuarios ya no podrán acceder a través del subdominio personalizado.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={changePortal.isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {changePortal.isLoading ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </>
  );
}

export default CompanyActions;
